import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import first from 'lodash/first';
import RsTableRow from '@Pages/RetroSynthesis/components/RsTable/RsTableRow';
import { Checkbox } from '@els/els-react--checkbox';
import RsProjectRouteRightHandLinks from '@Pages/RetroSynthesis/ProjectRoutes/components/RsProjectRouteRightHandLinks';
import RsRouteLabel from '@Pages/RetroSynthesis/ProjectRoutes/components/RsRouteLabel';
import {
  RouteType,
  PATH_PLANNER_SCALED_S_2STEPS,
  getProjectRoutesTypeFromInfoMapForAA,
  getProjectDetail,
} from '@Pages/RetroSynthesis/constants';
import RsBuildingBlocks from '@Pages/RetroSynthesis/components/RsBuildingBlocks';
import RouteThumbnailsTreeContainer from '@Pages/RetroSynthesis/RouteTree/RouteThumbnailsTreeContainer';
import { routeLayoutNest, routeLayoutPerformRouteName } from '@Pages/RetroSynthesis/ProjectRoutes/components/RsProjectRouteHelper';
import RsStructure from '@Pages/RetroSynthesis/components/RsStructure';
import { trimUptoTwoDecimals } from '../../utils';

import './RsProjectRouteItem.scss';

function RsProjectRouteItem({
  selected,
  id,
  routeType,
  stepCount,
  routeLayout = [],
  confidence,
  onSelectionChange = () => null,
  projectId,
  synthesisMenuOptions,
  onRouteViewClick,
  isConfidenceColumnShown,
  isFirstDisconnectionColumnShown,
  projectInfo,
}) {
  const structures = routeLayout.filter((routeItem) => routeItem.leafNode).map((routeItem) => routeItem.substance) || [];
  const target = routeLayout[0].substance;
  const treeItems = first(routeLayoutNest(routeLayout));
  const isPredicted = routeType === RouteType.PREDICTED;
  const isCombined = routeType === RouteType.COMBINED;
  const confidenceTrimmed = trimUptoTwoDecimals(confidence);
  const trackingContent = [
    {
      id: `project:${projectId}`,
      detail: getProjectDetail({ ...projectInfo, id: projectId }),
      type: getProjectRoutesTypeFromInfoMapForAA(projectInfo),
    },
    {
      id: `project:${projectId}`,
      id2: `route:${id}`, // route:<route_number>, Content ID2
      type: routeType ? RouteType.trackingMap[routeType] : '', // <route_type>, Content Type evar20
      datapoints: stepCount, // <number of steps> event 239, already implemented
    },
  ];
  return (
    <RsTableRow>
      <td className="table-row__select c-els-table__cell c-els-table__basic-cell">
        <Checkbox
          className="project-checkbox"
          onChange={(e) => {
            const { checked } = e.currentTarget;
            onSelectionChange &&
              e &&
              e.target &&
              id &&
              onSelectionChange({
                targetId: id,
                value: !!checked,
              });
          }}
          label={`project:${projectId}`}
          shouldHideLabel
          checked={selected}
        />
      </td>
      <td className="table-row__number c-els-table__cell c-els-table__basic-cell">
        <RsRouteLabel routeType={routeType} routeNo={id} />
      </td>
      <td className="table-row__blocks c-els-table__cell c-els-table__basic-cell">
        <RsBuildingBlocks
          routeType={routeType}
          structures={structures}
          stepCount={stepCount}
          synthesisMenuOptions={synthesisMenuOptions}
          imagePathKey={PATH_PLANNER_SCALED_S_2STEPS}
          trackingContent={trackingContent}
        />
      </td>
      {isFirstDisconnectionColumnShown && (
        <td className={cn('table-row__fd c-els-table__cell c-els-table__basic-cell')}>
          {(isPredicted || isCombined) && <RsStructure structure={target} showOptions={false} showDetails={false} />}
        </td>
      )}
      <td className="table-row__overview gray-cell c-els-table__cell c-els-table__basic-cell">
        <RouteThumbnailsTreeContainer
          tree={treeItems}
          list={routeLayout}
          routeName={routeLayoutPerformRouteName(routeType, id)}
          projectId={projectId}
          routeId={id}
        />
      </td>
      {isConfidenceColumnShown && (
        <td className={cn('table-row__confidence c-els-table__cell c-els-table__basic-cell', { 'gray-cell': isPredicted || isCombined })}>
          {(isPredicted || isCombined) && confidenceTrimmed}
        </td>
      )}
      <td className="table-row__details c-els-table__cell c-els-table__basic-cell">
        <RsProjectRouteRightHandLinks
          projectId={projectId}
          routeId={id}
          onRouteViewClick={onRouteViewClick}
          routeType={routeType}
          stepCount={stepCount}
        />
      </td>
    </RsTableRow>
  );
}

export const RouteItem = PropTypes.shape({
  id: PropTypes.number,
  substance: PropTypes.any,
  step: PropTypes.string,
  leafNode: PropTypes.bool,
  parentId: PropTypes.number,
});

RsProjectRouteItem.propTypes = {
  id: PropTypes.number,
  projectId: PropTypes.string,
  confidence: PropTypes.string,
  routeType: PropTypes.oneOf(RouteType.allTypes),
  stepCount: PropTypes.number,
  routeLayout: PropTypes.arrayOf(RouteItem),
  selected: PropTypes.bool,
  onSelectionChange: PropTypes.func,
  synthesisMenuOptions: PropTypes.array,
  onRouteViewClick: PropTypes.func,
  isConfidenceColumnShown: PropTypes.bool,
  isFirstDisconnectionColumnShown: PropTypes.bool,
  projectInfo: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

export default RsProjectRouteItem;
